import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Field, reduxForm, change } from 'redux-form';
import { Button, ButtonToolbar } from 'reactstrap';
import moment from 'moment';
import CurrencyUsdIcon from 'mdi-react/CurrencyUsdIcon';
import PoundIcon from 'mdi-react/PoundIcon';
import renderSelectField from '../../../../shared/components/form/Select';
import renderCheckBoxField from '../../../../shared/components/form/CheckBox';
import Tooltip from '../../../../shared/components/widgets/Tooltip';
import { renderFieldForm, RenderDatePicker } from '../../shared/formComponents';
import CollapsibleSection from './shared/CollapsibleSection';
import MpbtStaticMultiSelect from './shared/MpbtStaticMultiSelect';
import MpbtPlatformSettingsSection from './MpbtPlatformSettingsSection';
import { ToggleWrapOuter, renderToggleField } from './shared/ToggleFieldCss';
import {
  SETUP_FLOW_OPTIONS,
  AGENCY_INTERNAL_OPTIONS,
  STATE_OPTIONS,
  SPEND_SPEED_OPTIONS,
  GOOGLE_SPEND_TACTIC_OPTIONS,
  SPEND_PRIORITY_OPTIONS,
  COST_TARGET_TYPE_OPTIONS,
} from '../../shared/constants';
import {
  MPBT_BUDGET_COMBOS,
  MPBT_PLATFORM_LABELS,
  MPBT_LANGUAGE_OPTIONS,
} from '../../shared/mpbtConstants';

export const FORM_NAME = 'mpbtBudgetGroupCreate';

// Derive the list of selected combo objects from the individual "combo_<key>"
// checkbox fields (e.g. combo_google_search, combo_meta_facebook_instagram).
export const getSelectedCombos = (values) => (
  MPBT_BUDGET_COMBOS.filter(combo => values?.[`combo_${combo.key}`])
);

export const ACTIVE_DAY_FIELDS = [
  { label: 'Monday', name: 'monday' },
  { label: 'Tuesday', name: 'tuesday' },
  { label: 'Wednesday', name: 'wednesday' },
  { label: 'Thursday', name: 'thursday' },
  { label: 'Friday', name: 'friday' },
  { label: 'Saturday', name: 'saturday' },
  { label: 'Sunday', name: 'sunday' },
];

const cleanNumber = (value) => {
  if (value) {
    const match = value.toString().match(/^[0-9]*(?:\.\d{0,2})?/);
    return match ? match[0] : '';
  }
  return value;
};

const MultiPlatformBudgetToolForm = ({
  handleSubmit,
  disableButton,
  onCancel,
  formValues,
  user,
}) => {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const theme = useSelector(store => store.theme);
  const isDarkMode = theme.className && !theme.className.endsWith('light');
  const dispatch = useDispatch();
  const selectedCombos = getSelectedCombos(formValues);
  const selectedPlatforms = [...new Set(selectedCombos.map(combo => combo.platform))];

  return (
    <form className="form" onSubmit={handleSubmit}>
      <div className="form__form-group">
        <span className="form__form-group-label">
          BGD Name Prefix{' '}
          <Tooltip id="mpbtNamePrefix" msg="Each budget group will be saved as '{Prefix} - {Platform} {Budget Type}'" placement="top" />
        </span>
        <div className="form__form-group-field">
          <Field name="namePrefix" component={renderFieldForm} placeholder="e.g. BG Cavu 30d" />
        </div>
      </div>

      <div className="form__form-group">
        <span className="form__form-group-label">Platform - Budget Type (select all that apply)</span>
        {MPBT_BUDGET_COMBOS.map(combo => (
          <div key={combo.key} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ flex: '0 0 260px' }}>
              <Field
                name={`combo_${combo.key}`}
                component={renderCheckBoxField}
                label={combo.label}
              />
            </div>
            {formValues?.[`combo_${combo.key}`] && (
              <div className="form__form-group-field" style={{ flex: '0 0 200px' }}>
                <div className="form__form-group-icon"><CurrencyUsdIcon /></div>
                <Field
                  name={`combo_${combo.key}_dailyBudget`}
                  component={renderFieldForm}
                  format={cleanNumber}
                  placeholder={`Daily budget in ${selectedSeller.currencyCode}`}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {(selectedPlatforms.includes('google') || selectedPlatforms.includes('meta')) && (
        <>
          <div className="form__form-group">
            <span className="form__form-group-label">
              Landing Page / Store URL (Normalized - Google + Meta){' '}
              <Tooltip id="mpbtLandingPageUrl" msg="Shared landing page/store URL applied to every selected Google and Meta combo" placement="top" />
            </span>
            <div className="form__form-group-field">
              <Field name="landingPageUrl" component={renderFieldForm} placeholder="https://..." />
            </div>
          </div>

          <div className="form__form-group">
            <span className="form__form-group-label">
              Brand Name (Normalized - Google + Meta, optional){' '}
              <Tooltip id="mpbtBrandName" msg="Shared brand name applied to every selected Google and Meta combo" placement="top" />
            </span>
            <div className="form__form-group-field">
              <Field name="brandName" component={renderFieldForm} placeholder="Enter brand name" />
            </div>
          </div>

          <Field
            name="languages"
            component={MpbtStaticMultiSelect}
            label="Language(s) (Normalized - Google + Meta)"
            tooltip="Select one shared list of languages. Meta uses its locale code directly; Google's matching language constant is looked up automatically on save."
            placeholder="Select languages..."
            options={MPBT_LANGUAGE_OPTIONS}
          />
        </>
      )}

      <CollapsibleSection title="Setup Control" defaultOpen={false}>
        <div className="form__form-group">
          <span className="form__form-group-label">Budget Group Setup Flow</span>
          <div className="form__form-group-field">
            <Field name="setupFlow" component={renderSelectField} options={SETUP_FLOW_OPTIONS} />
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">View Toggle</span>
          <div className="form__form-group-field">
            <Field name="agencyInternal" component={renderSelectField} options={AGENCY_INTERNAL_OPTIONS} />
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Common Settings" defaultOpen>
        <div className="form__form-group">
          <span className="form__form-group-label">Allowed Days of the Week</span>
          {ACTIVE_DAY_FIELDS.map(day => (
            <Field
              key={day.name}
              name={day.name}
              component={renderCheckBoxField}
              label={day.label}
              defaultChecked
            />
          ))}
        </div>

        <div className="form__form-group">
          <div style={{ width: '50%', height: '60px', float: 'left' }}>
            <span className="form__form-group-label">Start Date</span>
            <Field
              name="startDate"
              handleDateChange={(newDate) => dispatch(change(FORM_NAME, 'startDate', newDate ? moment(newDate) : null))}
              component={RenderDatePicker}
              minDate={moment().startOf('day')}
            />
          </div>
          <div style={{ marginLeft: '50%', height: '60px' }}>
            <span className="form__form-group-label">End Date</span>
            <Field
              name="endDate"
              handleDateChange={(newDate) => dispatch(change(FORM_NAME, 'endDate', newDate ? moment(newDate) : null))}
              component={RenderDatePicker}
            />
          </div>
        </div>

        <div className="form__form-group">
          <span className="form__form-group-label">Budget Group State</span>
          <div className="form__form-group-field">
            <Field name="state" component={renderSelectField} options={STATE_OPTIONS} />
          </div>
        </div>

        <div className="form__form-group">
          <span className="form__form-group-label">Spend Speed</span>
          <div className="form__form-group-field">
            <Field name="spendSpeed" component={renderSelectField} options={SPEND_SPEED_OPTIONS} />
          </div>
        </div>

        <div className="form__form-group">
          <span className="form__form-group-label">Spend Tactic</span>
          <div className="form__form-group-field">
            <Field
              name="spendTactic"
              component={renderSelectField}
              // MPBT always uses "Purchase Full Funnel SA" regardless of platform
              // (including Amazon) - see GOOGLE_SPEND_TACTIC_OPTIONS in constants.js.
              options={GOOGLE_SPEND_TACTIC_OPTIONS}
            />
          </div>
        </div>

        <div className="form__form-group">
          <span className="form__form-group-label">Spend Priority</span>
          <div className="form__form-group-field">
            <Field name="spendPriority" component={renderSelectField} options={SPEND_PRIORITY_OPTIONS} />
          </div>
        </div>

        <div className="form__form-group">
          <span className="form__form-group-label">Cost Target Type</span>
          <div className="form__form-group-field">
            <Field name="costTargetType" component={renderSelectField} options={COST_TARGET_TYPE_OPTIONS} />
          </div>
        </div>
      </CollapsibleSection>

      {formValues?.costTargetType?.value === 'bnb' ? (
        <>
          <div className="form__form-group">
            <span className="form__form-group-label">Brand Cost Target (ROAS)</span>
            <div className="form__form-group-field">
              <div className="form__form-group-icon"><PoundIcon /></div>
              <Field name="costTargetBrand" type="text" component={renderFieldForm} />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">Non-Brand Cost Target (ROAS)</span>
            <div className="form__form-group-field">
              <div className="form__form-group-icon"><PoundIcon /></div>
              <Field name="costTargetNonBrand" type="text" component={renderFieldForm} />
            </div>
          </div>
        </>
      ) : (
        <div className="form__form-group">
          <span className="form__form-group-label">Cost Target (ROAS)</span>
          <div className="form__form-group-field">
            <div className="form__form-group-icon"><PoundIcon /></div>
            <Field name="costTarget" type="text" component={renderFieldForm} />
          </div>
        </div>
      )}

      {selectedPlatforms.map(platform => (
        <CollapsibleSection key={platform} title={`${MPBT_PLATFORM_LABELS[platform]} Settings`} defaultOpen={false}>
          <MpbtPlatformSettingsSection
            platform={platform}
            selectedComboKeys={selectedCombos.filter(combo => combo.platform === platform).map(combo => combo.key)}
            accountId={selectedSeller.value}
          />
        </CollapsibleSection>
      ))}

      {user?.access === 'admin' && (
        <div style={{ display: 'block', clear: 'both', width: '100%', marginBottom: '15px' }}>
          <ToggleWrapOuter style={{ justifyContent: 'center' }}>
            <Field
              name="triggerDefaultJobs"
              component={renderToggleField}
              label="Run Default Jobs"
              isDarkMode={isDarkMode}
            />
          </ToggleWrapOuter>
        </div>
      )}

      <ButtonToolbar className="form__button-toolbar">
        <Button color="primary" type="submit" disabled={disableButton}>Save All Selected</Button>
        <Button type="button" onClick={onCancel}>Cancel</Button>
      </ButtonToolbar>
    </form>
  );
};

MultiPlatformBudgetToolForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  disableButton: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  formValues: PropTypes.shape(),
  user: PropTypes.shape({
    access: PropTypes.string,
  }),
};

MultiPlatformBudgetToolForm.defaultProps = {
  disableButton: false,
  formValues: {},
  user: null,
};

const validate = (values) => {
  const errors = {};
  const selectedCombos = getSelectedCombos(values);
  const selectedPlatforms = [...new Set(selectedCombos.map(combo => combo.platform))];

  if (!selectedCombos.length) {
    MPBT_BUDGET_COMBOS.forEach(combo => {
      errors[`combo_${combo.key}`] = 'Select at least one Platform - Budget Type combo';
    });
  }

  selectedCombos.forEach(combo => {
    const dailyBudgetField = `combo_${combo.key}_dailyBudget`;
    if (!values[dailyBudgetField] || parseFloat(values[dailyBudgetField]) <= 0) {
      errors[dailyBudgetField] = 'Daily budget must be set and greater than 0';
    }
  });

  if (!values.namePrefix) {
    errors.namePrefix = 'Name Prefix is required';
  }
  if (!values.startDate) {
    errors.startDate = 'Start Date is required';
  }
  if (values.endDate && moment(values.startDate) >= moment(values.endDate)) {
    errors.endDate = 'End Date must be after Start Date';
  }
  if ((selectedPlatforms.includes('google') || selectedPlatforms.includes('meta')) && !values.landingPageUrl) {
    errors.landingPageUrl = 'Landing Page / Store URL is required for Google and/or Meta';
  }
  if ((selectedPlatforms.includes('google') || selectedPlatforms.includes('meta')) && !(values.languages || []).length) {
    errors.languages = 'Select at least one language for Google and/or Meta';
  }

  if (values.costTargetType?.value === 'bnb') {
    if (values.costTargetBrand === undefined || values.costTargetBrand === '' || parseFloat(values.costTargetBrand) < 0.333 || parseFloat(values.costTargetBrand) > 20) {
      errors.costTargetBrand = 'Brand Cost Target for ROAS must be between 0.333 and 20';
    }
    if (values.costTargetNonBrand === undefined || values.costTargetNonBrand === '' || parseFloat(values.costTargetNonBrand) < 0.333 || parseFloat(values.costTargetNonBrand) > 20) {
      errors.costTargetNonBrand = 'Non-Brand Cost Target for ROAS must be between 0.333 and 20';
    }
  } else if (values.costTarget === undefined || values.costTarget === '' || parseFloat(values.costTarget) < 0.333 || parseFloat(values.costTarget) > 20) {
    errors.costTarget = 'Cost Target for ROAS must be between 0.333 and 20';
  }

  return errors;
};

export default reduxForm({
  form: FORM_NAME,
  validate,
  enableReinitialize: true,
})(MultiPlatformBudgetToolForm);
